import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import { get } from 'lodash';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import Link from 'components/common/Link';
import IconButton from 'components/common/IconButton';
import IconArrowHeadBottom from 'components/icons/IconArrowHeadBottom';
import IconImage from 'components/icons/IconImage';
import DocumentsViewDialog from 'components/common/DocumentsViewDialog';

import patientDobToAge from 'utils/patientDobToAge';
import formatOrderNumber from 'utils/formatOrderNumber';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';
import formatSinglePlural from 'utils/formatSinglePlural';
import splitLongWords from 'utils/splitLongWords';

import hexToRgb from 'styles/hexToRgb';
import dateFormat from 'config/dateFormat';

const dateTimeSecondsFormat = 'hh:mm:ss a MM/DD/YYYY';
const notificationDateTimeFormat = 'MM/DD/YYYY hh:mm:ss a';

const styles = theme => ({
  row: {
    height: 20,
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      cursor: 'pointer',
    },
    '&:hover $cellMessage': {
      textShadow: '.4px 0 0 currentColor, -.4px 0 0  currentColor',
      color: theme.palette.primary[500],
    },
  },
  rowRealTime: {
    backgroundColor: `rgba(${hexToRgb(theme.palette.primary[500])}, 0.25)`,
  },
  rowHasRead: {
    '& $cellMessage': {
      color: theme.palette.grey[650],
    },
    '&:hover $cellMessage': {
      textShadow: 'none',
      color: theme.palette.grey[650],
    },
  },
  rowPatientNotificationsHasRead: {
    '& $cellMessage': {
      color: theme.palette.grey[650],
    },
    '&:hover $cellMessage': {
      textShadow: 'none',
      color: theme.palette.grey[650],
    },
  },
  rowPatientNotifications: {
    height: 30,
    backgroundColor: theme.palette.grey[100],
    borderTop: `2px solid ${theme.palette.common.white}`,
    '& $cellDate': {
      verticalAlign: 'top',
    },
  },
  rowHead: {
    borderTop: `1px solid ${theme.palette.primary.A100}`,
    '&:first-child': {
      borderTop: 'none',
    },
    '& $cell': {
      paddingTop: 18,
      fontSize: 12,
      letterSpacing: '0.03em',
      fontWeight: 700,
      whiteSpace: 'nowrap',
      color: theme.palette.grey[600],
    },
  },
  rowBody: {
    '& $cell': {
      paddingTop: 4,
      paddingBottom: 16,
    },
  },
  cell: {
    padding: '4px 8px 4px 8px',
    border: 'none',
    verticalAlign: 'top',
    '&:first-child': {
      paddingLeft: 4,
    },
    '&:last-child': {
      paddingRight: 4,
    },
    [theme.breakpoints.down('xs')]: {
      padding: '4px 4px 4px 4px',
    },
  },
  cellPatientNotifications: {
    verticalAlign: 'middle',
  },
  cellDate: {
    minWidth: 75,
  },
  cellPatient: {
    minWidth: 100,
  },
  cellMessage: {
    width: '95%',
    transition: theme.transitions.create(['color'], {
      duration: theme.transitions.duration.short,
    }),
  },
  linkPatient: {
    display: 'block',
    whiteSpace: 'nowrap',
    maxWidth: 150,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('xs')]: {
      maxWidth: 100,
      overflow: 'visible',
      whiteSpace: 'normal',
    },
  },
  cellActions: {
    width: 16,
    paddingRight: 16,
    '&:last-child': {
      paddingRight: 16,
    },
  },
  button: {
    width: 30,
    height: 30,
    margin: -16,
    verticalAlign: 'middle',
  },
  iconArrowHeadBottom: {
    width: 12,
    height: 8,
  },
  iconArrowHeadBottomDiffOpen: {
    transform: 'rotate(180deg)',
  },
  cellDiff: {
    paddingTop: 0,
    paddingBottom: 0,
    verticalAlign: 'top',
    color: theme.palette.grey[700],
    borderBottom: 'none',
  },
  cellDiffType: {
    width: 70,
  },
  iconImage: {
    width: 18,
    height: 18,
    verticalAlign: 'middle',
    marginTop: -1,
    marginRight: 2,
  },
});

class NotificationsItem extends Component {
  constructor(props) {
    super(props);

    this.state = {
      realTime: props.realTime,
      patientOpen: false,
      mmsViewDialogOpen: false,
      mmsViewDialogDocumentID: null,
    };
    this.onPatientNotificationsButtonClick = this.onPatientNotificationsButtonClick.bind(this);
    this.onMMSViewButtonClick = this.onMMSViewButtonClick.bind(this);
    this.onMMSViewDialogClose = this.onMMSViewDialogClose.bind(this);
  }

  componentDidMount() {
    setTimeout(() => {
      this.setState({ realTime: false });
    }, 2000);
  }

  onPatientNotificationsButtonClick() {
    const { actions, onPatientNotificationsLoad, Patient, realTime } = this.props;
    if (!this.props['Patient Notifications']
      || this.props['Patient Notifications'] && realTime) {
      onPatientNotificationsLoad({
        'Patient ID': Patient['Record Number'],
      });
    }
    this.setState({ patientOpen: !this.state.patientOpen });
  }

  onMMSViewDialogClose() {
    this.setState({
      mmsViewDialogOpen: false,
      mmsViewDialogDocumentID: null,
    });
  }

  onMMSViewButtonClick(mmsViewDialogDocumentID) {
    this.setState({
      mmsViewDialogOpen: true,
      mmsViewDialogDocumentID,
    });
  }

  render() {
    const { Subject, Patient, Meta, Count, business, onClick, onPatientItemClick, classes, ...otherProps } = this.props;
    const { realTime, patientOpen, mmsViewDialogOpen, mmsViewDialogDocumentID } = this.state;
    let mmsViewDialogDocument;
    
    const getNotificationMessage = (notification) => {
      const notificationType = [notification.Subject, notification['Event Type'], notification['Sub Subject']].filter(item => item).join(' ');
      const authorName = notification.Author && notification.Author.ID ? `${notification.Author['First Name']} ${notification.Author['Last Name']}` : 'Unspecified user';
      const messageType = notification.Meta['Document ID'] ? 'MMS' : 'SMS'
      let notificationMessage;
      let daysLabel;
        switch (notificationType) {
          case 'patient create':
            notificationMessage = `${authorName} created new Patient chart`;
          break;
          case 'patient delete':
            notificationMessage = `${authorName} deleted Patient chart`;
          break;
          case 'patient create patient_sms': 
            if (messageType === 'MMS') {
              notificationMessage = notification.Meta.Message
                ? (<Fragment>
                  {`${authorName} sent `}
                  <Link
                    color="primary"
                    breakWord
                    onClick={() => this.onMMSViewButtonClick(notification.Meta['Document ID'])}
                    underline
                  >
                    <IconImage className={classes.iconImage} />
                    MMS
                  </Link>
                  {` to Patient: "${notification.Meta.Message}"`}
                </Fragment>)
                : (<Fragment>
                  {`${authorName} sent `}
                  <Link
                    color="primary"
                    breakWord
                    onClick={() => this.onMMSViewButtonClick(notification.Meta['Document ID'])}
                    underline
                  >
                    <IconImage className={classes.iconImage} />
                    MMS
                  </Link>
                  {` to Patient`}
                </Fragment>);
            } else {
              notificationMessage = notification.Meta.Message ? `${authorName} sent SMS to Patient: "${notification.Meta.Message}"` : `${authorName} sent SMS to Patient`;
            }   
          break;
          case 'patient receive patient_sms':
            if (messageType === 'MMS') {
              notificationMessage = notification.Meta.Message
                ? (<Fragment>
                  {`Recieved new `}
                  <Link
                    color="primary"
                    breakWord
                    onClick={() => this.onMMSViewButtonClick(notification.Meta['Document ID'])}
                    underline
                  >
                    <IconImage className={classes.iconImage} />
                    MMS
                  </Link>
                  {`: "${notification.Meta.Message}"`}
                </Fragment>)
                : (<Fragment>
                  {`Recieved new `}
                  <Link
                    color="primary"
                    breakWord
                    onClick={() => this.onMMSViewButtonClick(notification.Meta['Document ID'])}
                    underline
                  >
                    <IconImage className={classes.iconImage} />
                    MMS
                  </Link>
                </Fragment>);
            } else {
              notificationMessage = notification.Meta.Message ? `Recieved new SMS: "${notification.Meta.Message}"` : `Recieved new SMS`;
            }
          break;
          case 'patient create patient_document':
            notificationMessage = `${authorName} added new Document "${splitLongWords(notification.Meta['File Name'])}"`
          break;
          case 'patient delete patient_document':
            notificationMessage = `${authorName} deleted Document "${splitLongWords(notification.Meta['File Name'])}"`
          break;
          case 'patient create patient_notes':
            notificationMessage = `${authorName} added new Note "${notification.Meta.Text}"`
          break;
          case 'patient update patient_notes':
            notificationMessage = `${authorName} updated Note "${notification.Meta.Text}"`
          break;
          case 'patient delete patient_notes':
            notificationMessage = `${authorName} deleted Note "${notification.Meta.Text}"`
          break;
          case 'order create':
            notificationMessage = (
              <Fragment>
                {`${authorName} created new Order `}
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${notification.Meta['Order ID']}`}
                  target="_blank"
                  underline
                >
                  {`#${formatOrderNumber(notification.Meta['Order ID'])}`}
                </Link>
              </Fragment>
            );
          break;
          case 'order delete':
            notificationMessage = `${authorName} deleted Order #${formatOrderNumber(notification.Meta['Order ID'])}`
          break;
          case 'order create order_notes':
            notificationMessage = (
              <Fragment>
                {`${authorName} added new Note "${notification.Meta.Text}" in Order `}
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${notification.Meta['Order ID']}`}
                  target="_blank"
                  underline
                >
                  {`#${formatOrderNumber(notification.Meta['Order ID'])}`}
                </Link>
              </Fragment>
            );
          break;
          case 'order update order_notes':
            notificationMessage = (
              <Fragment>
                {`${authorName} updated Note "${notification.Meta.Text}" in Order `}
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${notification.Meta['Order ID']}`}
                  target="_blank"
                  underline
                >
                  {`#${formatOrderNumber(notification.Meta['Order ID'])}`}
                </Link>
              </Fragment>
            );
          break;
          case 'order delete order_notes':
            notificationMessage = (
              <Fragment>
                {`${authorName} deleted Note "${notification.Meta.Text}" in Order `}
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${notification.Meta['Order ID']}`}
                  target="_blank"
                  underline
                >
                  {`#${formatOrderNumber(notification.Meta['Order ID'])}`}
                </Link>
              </Fragment>
            );
          break;
          case 'order remind authorization':
            daysLabel = formatSinglePlural({
              quantity: notification.Meta['Days Left'],
              single: 'day',
              plural: 'days',
            });
            notificationMessage = (
              <Fragment>
                {`Prior Authorization expires in ${notification.Meta['Days Left']} ${daysLabel} in Order `}
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${notification.Meta['Order ID']}`}
                  target="_blank"
                  underline
                >
                  {`#${formatOrderNumber(notification.Meta['Order ID'])}`}
                </Link>
              </Fragment>
            );
          break;
          case 'order remind date_of_service':
            daysLabel = formatSinglePlural({
              quantity: notification.Meta['Days Left'],
              single: 'day',
              plural: 'days',
            });
            notificationMessage = (
              <Fragment>
                {`Date of Service expires in ${notification.Meta['Days Left']} ${daysLabel} in Order `}
                <Link
                  color="primary"
                  breakWord
                  href={`/workflow/order/edit/${notification.Meta['Order ID']}`}
                  target="_blank"
                  underline
                >
                  {`#${formatOrderNumber(notification.Meta['Order ID'])}`}
                </Link>
              </Fragment>
            );
          break;
          default:
          notificationMessage = notificationType;
      }
      return notificationMessage;
    };
    if (mmsViewDialogDocumentID) {
      if (Meta['Document ID'] && Meta['Document ID'] === mmsViewDialogDocumentID) {
        mmsViewDialogDocument = {
          ID: mmsViewDialogDocumentID,
          'File Url': Meta['File Url'],
          'File Name': Meta['File Name'],
          'File Type': Meta['File Type'],
        };
      } else if (this.props['Patient Notifications']) {
        this.props['Patient Notifications'].forEach(patientNotificationsItem => {
          if (patientNotificationsItem.Meta['Document ID']
            && patientNotificationsItem.Meta['Document ID'] === mmsViewDialogDocumentID) {
            mmsViewDialogDocument = {
              ID: mmsViewDialogDocumentID,
              'File Url': patientNotificationsItem.Meta['File Url'],
              'File Name': patientNotificationsItem.Meta['File Name'],
              'File Type': patientNotificationsItem.Meta['File Type'],
            };
          }
        });
      }
    }
    return (
      <Fragment>
        <TableRow
          onClick={onClick}
          className={classNames(classes.row, {
            [classes.rowRealTime]: realTime,
            [classes.rowHasRead]: otherProps['Has Read'],
          })}
        >
          <TableCell className={classNames(classes.cell, classes.cellDate)}>
            <Typography variant="caption">
              {formatUtcToLocalDate(
                otherProps['Create At'],
                dateTimeSecondsFormat,
                notificationDateTimeFormat,
                business['Company Address']['Time Zone'],
                business['Company Address']['Observes Daylight Saving'],
              )}
            </Typography>
          </TableCell>
          <TableCell className={classNames(classes.cell, classes.cellPatient)}>
            {(Patient && Patient['Record Number']) && (
              <Fragment>
                {[Subject, otherProps['Event Type'], otherProps['Sub Subject']].filter(item => item).join(' ') === 'patient delete' ? (
                  `${Patient['First Name']} ${Patient['Last Name']}`
                ) : (
                  <Link
                    className={classes.linkPatient}
                    color="primary"
                    breakWord
                    href={`/charts/${Patient['Record Number']}/profile`}
                    target="_blank"
                    underline
                  >
                    {`${Patient['First Name']} ${Patient['Last Name']}`}
                  </Link>
                )}
                {Patient['Date of Birth'] && (
                  <Typography variant="caption">
                    {Patient['Date of Birth']}
                    {', '}
                    {patientDobToAge(Patient['Date of Birth'])}
                  </Typography>
                )}
              </Fragment>
            )}
          </TableCell>
          <TableCell className={classNames(classes.cell, classes.cellMessage)}>
            {getNotificationMessage(this.props)}
          </TableCell>
          <TableCell
            className={classNames(classes.cell, classes.cellActions)}
          >
            {Count > 1 &&
              <IconButton
                className={classes.button}
                onClick={this.onPatientNotificationsButtonClick}
                tooltipContent="All patient notifications"
              >
                <IconArrowHeadBottom
                  className={classNames(classes.iconArrowHeadBottom, {
                    [classes.iconArrowHeadBottomDiffOpen]: patientOpen,
                  })}
                />
              </IconButton>
            }
          </TableCell>
        </TableRow>
        {(patientOpen && this.props['Patient Notifications']) &&
          <Fragment>
            {this.props['Patient Notifications'].map(patientNotificationsItem => (
              <TableRow
                className={classNames(classes.row, classes.rowPatientNotifications, {
                  [classes.rowPatientNotificationsHasRead]: patientNotificationsItem['Has Read'],
                })}
                key={patientNotificationsItem.ID}
                onClick={() => {
                  onPatientItemClick(patientNotificationsItem);
                }}
              >
                <TableCell className={classes.cell} />
                <TableCell className={classNames(classes.cell, classes.cellPatientNotifications, classes.cellDate)}>
                  <Typography variant="caption">
                    {formatUtcToLocalDate(
                      patientNotificationsItem['Create At'],
                      dateTimeSecondsFormat,
                      notificationDateTimeFormat,
                      business['Company Address']['Time Zone'],
                      business['Company Address']['Observes Daylight Saving'],
                    )}
                  </Typography>
                </TableCell>
                <TableCell className={classNames(classes.cell, classes.cellPatientNotifications, classes.cellMessage)} colSpan={2}>
                  {getNotificationMessage(patientNotificationsItem)}
                </TableCell>
              </TableRow>
            ))}
          </Fragment>
        }
        {mmsViewDialogDocumentID &&
          <DocumentsViewDialog
            open={mmsViewDialogOpen}
            onClose={this.onMMSViewDialogClose}
            Documents={[mmsViewDialogDocument]}
            initialDocumentID={mmsViewDialogDocumentID}
          />
        }
      </Fragment>
    );
  }
}

NotificationsItem.propTypes = {
  realTime: PropTypes.bool,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  onPatientItemClick: PropTypes.func,
  classes: PropTypes.shape({
    row: PropTypes.string.isRequired,
    cell: PropTypes.string.isRequired,
  }).isRequired,
};

NotificationsItem.defaultProps = {
  realTime: false,
  business: {},
  onPatientItemClick: () => {},
};

export default withStyles(styles)(NotificationsItem);



// WEBPACK FOOTER //
// ./src/components/home/Notifications/NotificationsItem.js